// `AccountsListPage` — US-166..US-175 + US-180 (read side).
//
// Mounted at `/accounts` inside the `_authed` shell. Composes the DS-D
// `AccountCard` for each connected LinkedIn account and surfaces:
//
//   - Header with title + count pill (count / quota) + "Connect account" CTA.
//   - Per-card status pill, status banner (US-168), stat pills (US-175).
//   - Per-card actions (Pause/Resume/Reconnect/Edit voice/Disconnect).
//   - Plan-quota gate via `PLAN_LIMITS` (US-167) — disables the CTA + swaps
//     the footer to "Limite atteinte".
//   - US-174 confirmation dialog for Disconnect.
//
// Mutations route through `useUpdateLinkedinAccount` (Pause/Resume share the
// `status` field), `useReconnectAccount` (extension-driven recovery, US-171),
// and `useDeleteLinkedinAccount`.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  useDeleteLinkedinAccount,
  useBilling,
  useLinkedinAccounts,
  useUpdateLinkedinAccount,
  useUserHubChannel,
  type ApiError,
  type LinkedinAccountRow,
} from '@barel/api-client/react';
import { useQueryClient } from '@tanstack/react-query';
import { PLAN_LIMITS, type Plan } from '@barel/core';
import { useT } from '@barel/i18n';
import {
  AccountCard,
  Banner,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Pill,
  Spinner,
  toast,
  type AccountFixture,
  type AccountStatus,
} from '@barel/ui';
import { useNavigate } from '@tanstack/react-router';

import { useAccountCardLabels, useDefaultStatusInfo } from './lib/labels';
import { useReconnectAccount } from './lib/useReconnectAccount';

export interface AccountsListPageProps {
  /** Optional override — used by tests + Storybook. */
  plan?: Plan;
  /** Override the default `/accounts/connect` navigation. */
  onConnectAccount?: () => void;
  /** Override the per-card "Edit voice" deep-link. */
  onEditVoice?: (account: LinkedinAccountRow) => void;
}

function rowToFixture(row: LinkedinAccountRow): AccountFixture {
  return {
    id: row.id,
    name: row.name,
    initials: row.initials,
    identityColor: row.color,
    url: row.vanitySlug ? `linkedin.com/in/${row.vanitySlug}` : '',
    voice: row.voiceLabel ?? '—',
    avatarUrl: row.avatarUrl ?? undefined,
    businesses: 0,
    campaigns: 0,
    salesNav: row.salesNav,
  };
}

/**
 * Convert a server timestamp (ISO string or epoch number) to epoch ms.
 * Falls back to `0` so the caller can treat it as "infinitely in the past".
 */
function toEpochMs(ts: string | number | null | undefined): number {
  if (typeof ts === 'number') return ts;
  if (typeof ts === 'string') {
    const parsed = Date.parse(ts);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function AccountsListPage({
  plan: planOverride,
  onConnectAccount: onConnectOverride,
  onEditVoice: onEditVoiceOverride,
}: AccountsListPageProps = {}): ReactNode {
  const t = useT('accounts');
  const navigate = useNavigate();
  const labels = useAccountCardLabels();
  const defaultStatusInfo = useDefaultStatusInfo();

  const qc = useQueryClient();
  const accountsQuery = useLinkedinAccounts();
  const billingQuery = useBilling();
  const updateAccount = useUpdateLinkedinAccount();
  const deleteAccount = useDeleteLinkedinAccount();
  const reconnectAccount = useReconnectAccount();

  // Realtime: SW-driven `/ext/account/health` updates emit `account.status`
  // through UserHub. Without this subscription the page would freeze on the
  // last cached row (the reconnect mutation only invalidates once on settle).
  useUserHubChannel('account.status', () => {
    void qc.invalidateQueries({ queryKey: ['linkedin-accounts'] });
  });

  // US-172 — periodic ticker so the auto-resume countdown re-renders without
  // each card owning its own setInterval. Once-per-minute resolution keeps
  // wakeups cheap; the AccountCard already rounds at minute granularity.
  const [tickNow, setTickNow] = useState(() => Date.now());
  useEffect(() => {
    const hasLimited = (accountsQuery.data?.accounts ?? []).some((acc) => acc.status === 'limited');
    if (!hasLimited) return;
    const id = window.setInterval(() => setTickNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [accountsQuery.data?.accounts]);

  const [confirmDisconnect, setConfirmDisconnect] = useState<LinkedinAccountRow | null>(null);
  // US-171: short-lived flag that flashes the `reconnecting` pill on click
  // for the ~hundreds-of-ms window before the SW has POSTed
  // `error/reconnecting`. After the bridge window, the cached row drives the
  // pill — `statusInfo === 'reconnecting'` keeps it lit until the SW
  // transitions onward. This avoids any stuck-flag failure modes (clock
  // skew, missed events) because the server is the single source of truth
  // beyond the bridge.
  const [reconnectingId, setReconnectingId] = useState<string | null>(null);

  // Billing owns subscription entitlements. The auth `me` envelope deliberately
  // contains only the user/session and must not be used to infer plan quotas.
  const plan: Plan = planOverride ?? billingQuery.data?.trial.plan ?? 'discover';
  const quota = PLAN_LIMITS[plan].accounts;
  const accounts = accountsQuery.data?.accounts ?? [];
  const count = accounts.length;
  const atQuota = count >= quota;

  const handleConnect = useCallback(() => {
    if (onConnectOverride) {
      onConnectOverride();
    } else {
      void navigate({ to: '/accounts/connect' });
    }
  }, [onConnectOverride, navigate]);

  const handlePause = useCallback(
    async (row: LinkedinAccountRow) => {
      try {
        await updateAccount.mutateAsync({
          id: row.id,
          status: 'paused',
          statusInfo: t('card.statusInfo.paused'),
        });
        toast.success({ title: t('actionsToast.pausedTitle') });
      } catch (err) {
        const apiErr = err as ApiError;
        toast.error({
          title: t('actionsToast.errorTitle'),
          description: apiErr?.message ?? t('actionsToast.errorBody'),
        });
      }
    },
    [updateAccount, t],
  );

  const handleResume = useCallback(
    async (row: LinkedinAccountRow) => {
      try {
        await updateAccount.mutateAsync({ id: row.id, status: 'active', statusInfo: '' });
        toast.success({ title: t('actionsToast.resumedTitle') });
      } catch (err) {
        const apiErr = err as ApiError;
        toast.error({
          title: t('actionsToast.errorTitle'),
          description: apiErr?.message ?? t('actionsToast.errorBody'),
        });
      }
    },
    [updateAccount, t],
  );

  const handleReconnect = useCallback(
    (row: LinkedinAccountRow) => {
      // US-171 — dispatch via postmessage-bridge so the extension SW opens
      // the LinkedIn tab and the heartbeat re-validates. The optimistic
      // flag bridges only the click→SW-POST window; after that the cached
      // `statusInfo` drives the pill (see the status derivation below).
      setReconnectingId(row.id);
      reconnectAccount.reconnect(row.id);
    },
    [reconnectAccount],
  );

  // Drop the optimistic flag after a short bridge window. The SW POSTs
  // `error/reconnecting` within ~hundreds of ms, after which the cached
  // `statusInfo` drives the pill — and the parent's `serverStatus`/
  // `statusInfo` derivation (below) keeps showing `reconnecting` for as
  // long as the server-side row says so. Keying this effect only on
  // `reconnectingId` prevents repeated cache invalidations from resetting
  // the timer.
  useEffect(() => {
    if (!reconnectingId) return;
    const timer = window.setTimeout(() => setReconnectingId(null), 3_000);
    return () => window.clearTimeout(timer);
  }, [reconnectingId]);

  const handleEditVoice = useCallback(
    (row: LinkedinAccountRow) => {
      if (onEditVoiceOverride) {
        onEditVoiceOverride(row);
        return;
      }
      // Wave B5 wires the voice editor at `/voice/$linkedinAccountId`.
      void navigate({
        to: '/voice/$linkedinAccountId',
        params: { linkedinAccountId: row.id },
      });
    },
    [navigate, onEditVoiceOverride],
  );

  const handleDisconnect = useCallback(
    async (row: LinkedinAccountRow) => {
      setConfirmDisconnect(null);
      try {
        await deleteAccount.mutateAsync({ id: row.id });
        toast.success({ title: t('disconnect.successTitle') });
      } catch (err) {
        const apiErr = err as ApiError;
        toast.error({
          title: t('disconnect.errorTitle'),
          description: apiErr?.message ?? t('actionsToast.errorBody'),
        });
      }
    },
    [deleteAccount, t],
  );

  const countPill = useMemo<ReactNode>(
    () => (
      <Pill tone={atQuota ? 'neutral' : 'accent'} size="sm" variant="soft">
        {t('page.countLabel', { count, quota })}
      </Pill>
    ),
    [atQuota, count, quota, t],
  );

  const headerActions = useMemo<ReactNode>(
    () => (
      <Button
        variant="primary"
        size="md"
        onClick={handleConnect}
        disabled={atQuota}
        data-testid="accounts-connect-cta"
        aria-label={t('page.addAccountAria')}
        title={atQuota ? t('page.atQuotaTooltip', { quota }) : undefined}
      >
        {t('page.addAccount')}
      </Button>
    ),
    [atQuota, handleConnect, quota, t],
  );

  return (
    <section data-feature="accounts" className="flex min-h-full flex-col">
      <PageHeader title={t('page.title')} subtitle={countPill} actions={headerActions} />

      <div className="flex flex-col gap-3 px-6 py-5">
        {accountsQuery.isPending ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-secondary">
            <Spinner size="sm" tone="accent" /> {t('page.loading')}
          </div>
        ) : accountsQuery.isError ? (
          <Banner tone="error" title={t('page.errorTitle')}>
            {t('page.errorBody')}
          </Banner>
        ) : count === 0 ? (
          <EmptyState
            title={t('page.emptyTitle')}
            description={t('page.emptyBody')}
            action={
              <Button variant="primary" size="md" onClick={handleConnect}>
                {t('page.addAccount')}
              </Button>
            }
          />
        ) : (
          accounts.map((row) => {
            const fixture = rowToFixture(row);
            const serverStatus = (row.status as AccountStatus) ?? 'active';
            // US-172 — derive auto-resume target only for `limited` rows.
            // statusChangedAt is the server timestamp at which LinkedIn
            // returned the rate-limit; the spec promises a 2h window.
            const autoResumeAt =
              serverStatus === 'limited'
                ? toEpochMs(row.statusChangedAt) + 2 * 60 * 60 * 1000
                : undefined;
            void tickNow;
            // Pill priority (US-171):
            //   1. Optimistic flag (covers the click → SW-POST gap, ~3s).
            //   2. Server `statusInfo === 'reconnecting'` (covers the entire
            //      time the SW is in the reconnecting state — until the SW
            //      transitions onward via voyager success / 401 / etc).
            //   3. Otherwise fall through to whatever the server says.
            const status: AccountStatus =
              row.id === reconnectingId
                ? 'reconnecting'
                : serverStatus === 'error' && row.statusInfo === 'reconnecting'
                  ? 'reconnecting'
                  : serverStatus;
            return (
              <AccountCard
                key={row.id}
                account={fixture}
                status={status}
                statusInfo={row.statusInfo ?? undefined}
                defaultStatusInfo={defaultStatusInfo}
                mode={row.aiMode}
                offTargetSettings={{
                  showOffTarget: row.showOffTarget ?? false,
                  offTargetMode: row.offTargetMode,
                }}
                autoLeadDetect={row.autoLeadDetect ?? false}
                labels={labels}
                onPause={() => {
                  void handlePause(row);
                }}
                onResume={() => {
                  void handleResume(row);
                }}
                onReconnect={() => handleReconnect(row)}
                onEditVoice={() => handleEditVoice(row)}
                onDisconnect={() => setConfirmDisconnect(row)}
                autoResumeAt={autoResumeAt}
              />
            );
          })
        )}

        {/* Footer hint / limit-reached card */}
        {count > 0 && atQuota ? (
          <Card padding="md" radius="xl" data-region="quota-reached">
            <div className="flex flex-col gap-2 text-center">
              <p className="text-sm font-semibold text-secondary">
                {t('page.limitReachedTitle', { quota })}
              </p>
              <p className="text-xs text-tertiary">
                {t('page.limitReachedBody')}{' '}
                <a href="mailto:hello@barel.app" className="font-semibold text-accent no-underline">
                  {t('page.limitReachedLink')}
                </a>
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  void navigate({ to: '/settings/$section', params: { section: 'billing' } });
                }}
                data-action="upgrade-plan"
              >
                {t('page.limitReachedUpgrade')}
              </Button>
            </div>
          </Card>
        ) : count > 0 && !atQuota ? (
          <Card padding="md" radius="xl" data-region="quota-hint">
            <div className="flex flex-col gap-2 text-center">
              <p className="text-xs text-tertiary">
                {t('page.footerHint', { remaining: quota - count })}
              </p>
              <Button variant="ghost" size="sm" onClick={handleConnect}>
                {t('page.footerCta')}
              </Button>
            </div>
          </Card>
        ) : null}
      </div>

      {/* Disconnect confirmation (US-174) */}
      <DisconnectConfirmationModal
        row={confirmDisconnect}
        loading={deleteAccount.isPending}
        onCancel={() => setConfirmDisconnect(null)}
        onConfirm={() => {
          if (confirmDisconnect) {
            void handleDisconnect(confirmDisconnect);
          }
        }}
      />
    </section>
  );
}

interface DisconnectConfirmationModalProps {
  row: LinkedinAccountRow | null;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function DisconnectConfirmationModal({
  row,
  loading,
  onCancel,
  onConfirm,
}: DisconnectConfirmationModalProps): ReactNode {
  const t = useT('accounts');
  if (!row) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="disconnect-modal-title"
      data-testid="disconnect-confirm"
      className="fixed inset-0 z-modal flex items-center justify-center bg-scrim-modal p-4"
    >
      <Card padding="lg" radius="2xl" className="max-w-md w-full">
        <div className="flex flex-col gap-4">
          <h2 id="disconnect-modal-title" className="text-lg font-semibold text-primary">
            {t('disconnect.title', { name: row.name })}
          </h2>
          <p className="text-sm text-secondary">{t('disconnect.body')}</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="md" onClick={onCancel}>
              {t('disconnect.cancel')}
            </Button>
            <Button
              variant="danger"
              size="md"
              onClick={onConfirm}
              loading={loading}
              data-action="confirm-disconnect"
            >
              {t('disconnect.confirm')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
